// src/lib/legibleAccent.ts
// -----------------------------------------------------------------------------
// legibleAccent(accent, background) -> adapted accent hex.
//
// D-066 (DBOS-wide rule): a tenant's brand accent rendered against a given
// background is nudged in HSL LIGHTNESS only - hue and saturation preserved -
// until it clears a minimum contrast against that background. Lightened on a
// dark background, darkened on a light one. Already-legible accents pass through
// unchanged. Pure function: hex + hex -> hex, no side effects.
//
// This is the standalone primitive meant to lift into a shared DBOS util and be
// reused by every surface that renders a brand colour (SmartShop, SmartMail,
// SmartWeb, SmartShow). Keep it dependency-free.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) { const v = l * 255; return [v, v, v]; }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3) * 255, hue2rgb(p, q, h) * 255, hue2rgb(p, q, h - 1 / 3) * 255];
}

// relative luminance (WCAG) for contrast ratio
function luminance([r, g, b]: [number, number, number]): number {
  const a = [r, g, b].map(v => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

function contrast(a: [number, number, number], b: [number, number, number]): number {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const MIN_CONTRAST = 4.0;   // WCAG-ish threshold (D-066)

export function legibleAccent(accent: string, background: string): string {
  try {
    const bgRgb = hexToRgb(background);
    let accRgb = hexToRgb(accent);
    if (contrast(accRgb, bgRgb) >= MIN_CONTRAST) return accent;   // already legible

    const [h, s] = rgbToHsl(...accRgb);
    let [, , l] = rgbToHsl(...accRgb);
    const bgIsDark = luminance(bgRgb) < 0.5;
    const step = bgIsDark ? 0.04 : -0.04;   // lighten on dark, darken on light

    for (let i = 0; i < 24; i++) {
      l = Math.max(0, Math.min(1, l + step));
      accRgb = hslToRgb(h, s, l);
      if (contrast(accRgb, bgRgb) >= MIN_CONTRAST) break;
      if (l <= 0 || l >= 1) break;
    }
    return rgbToHex(...accRgb);
  } catch {
    return accent;   // never throw - a colour must always come back
  }
}
